import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE } from '../config/api';

const pedidoAdminInicial = {
  tipo_pedido: 'Soporte Técnico',
  colegio_id: '',
  sala_id: '',
  equipo_id: '',
  descripcion_problema: '',
};

const ListaPedidos = () => {
  const [pedidos, setPedidos] = useState([]);
  const [colegios, setColegios] = useState([]);
  const [tecnicos, setTecnicos] = useState([]);
  const [salasAdmin, setSalasAdmin] = useState([]);
  const [equiposAdmin, setEquiposAdmin] = useState([]);
  const [componentes, setComponentes] = useState([]);
  const [stockDepositos, setStockDepositos] = useState([]);
  const [depositoTecnico, setDepositoTecnico] = useState(null);
  const [estadosFiltro, setEstadosFiltro] = useState([]);
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [colegioFiltro, setColegioFiltro] = useState('');
  const [tecnicoFiltro, setTecnicoFiltro] = useState('');
  const [busquedaColegioFiltro, setBusquedaColegioFiltro] = useState('');
  const [colegioFiltroAbierto, setColegioFiltroAbierto] = useState(false);
  const [pedidoSolicitud, setPedidoSolicitud] = useState(null);
  const [detalleSolicitud, setDetalleSolicitud] = useState({ componente_id: '', cantidad: 1 });
  const [detallesSolicitud, setDetallesSolicitud] = useState([]);
  const [observacionSolicitud, setObservacionSolicitud] = useState('');
  const [enviandoSolicitud, setEnviandoSolicitud] = useState(false);
  const [solicitudesPorPedido, setSolicitudesPorPedido] = useState({});
  const [pedidoAdminAbierto, setPedidoAdminAbierto] = useState(false);
  const [pedidoAdmin, setPedidoAdmin] = useState(pedidoAdminInicial);
  const [guardandoPedidoAdmin, setGuardandoPedidoAdmin] = useState(false);
  const [pedidoEstado, setPedidoEstado] = useState(null);
  const [estadoForm, setEstadoForm] = useState({ estado: 'resuelto', observacion: '' });
  const [guardandoEstado, setGuardandoEstado] = useState(false);

  const estadosDisponibles = [
    { id: 'pendiente', label: 'Pendiente' },
    { id: 'en_espera', label: 'En espera' },
    { id: 'en_laboratorio', label: 'En laboratorio' },
    { id: 'resuelto', label: 'Resuelto' },
  ];

  // ✅ Detectar rol (admin/tecnico) para ocultar "Eliminar"
  const esTecnico = useMemo(() => {
    const rol = (localStorage.getItem('rolAdmin') || '').toLowerCase();
    return rol === 'tecnico';
  }, []);

  const esAdmin = useMemo(() => {
    const rol = (localStorage.getItem('rolAdmin') || '').toLowerCase();
    return rol === 'admin';
  }, []);

  // ✅ Etiquetas + estilos para estados
  const etiquetaEstado = (estadoRaw) => {
    const e = String(estadoRaw || '').toLowerCase();
    return (
      {
        pendiente: 'Pendiente',
        en_espera: 'En espera',
        en_laboratorio: 'En laboratorio',
        resuelto: 'Resuelto',
      }[e] || estadoRaw
    );
  };

  const badgeEstadoClass = (estadoRaw) => {
    const e = String(estadoRaw || '').toLowerCase();
    if (e === 'resuelto') return 'bg-green-100 text-green-800';
    if (e === 'en_laboratorio') return 'bg-purple-100 text-purple-800';
    if (e === 'en_espera') return 'bg-orange-100 text-orange-800';
    return 'bg-yellow-100 text-yellow-800'; // pendiente u otro
  };

  const tecnicoResolvioTexto = (pedido) => {
    if (String(pedido?.estado || '').toLowerCase() !== 'resuelto') return '';
    return pedido?.tecnico_resolvio || 'Sin registro';
  };

  const fechaResueltoTexto = (pedido) => {
    if (String(pedido?.estado || '').toLowerCase() !== 'resuelto' || !pedido?.fecha_resuelto) return '';
    return new Date(pedido.fecha_resuelto).toLocaleString();
  };

  const EstadoPedido = ({ pedido }) => {
    const tecnicoResolvio = tecnicoResolvioTexto(pedido);
    const fechaResuelto = fechaResueltoTexto(pedido);

    return (
      <div className="inline-flex flex-col items-start gap-1">
        <span
          className={`inline-flex items-center px-2 py-1 rounded text-sm ${badgeEstadoClass(
            pedido.estado
          )}`}
        >
          {etiquetaEstado(pedido.estado)}
        </span>
        {tecnicoResolvio && (
          <span className="text-xs text-gray-500 leading-tight">
            Resuelto por: <span className="font-semibold text-gray-700">{tecnicoResolvio}</span>
          </span>
        )}
        {fechaResuelto && (
          <span className="text-xs text-gray-500 leading-tight">Resuelto: {fechaResuelto}</span>
        )}
        {pedido?.tecnico_asignado && (
          <span className="text-xs text-blue-700 leading-tight">
            Asignado a: <span className="font-semibold">{pedido.tecnico_asignado}</span>
          </span>
        )}
      </div>
    );
  };

  const badgeSolicitudClass = (estadoRaw) => {
    const e = String(estadoRaw || '').toLowerCase();
    if (e === 'aprobada') return 'bg-blue-100 text-blue-800';
    if (e === 'utilizada') return 'bg-green-100 text-green-800';
    if (e === 'rechazada') return 'bg-red-100 text-red-800';
    if (e === 'cancelada') return 'bg-gray-100 text-gray-700';
    return 'bg-yellow-100 text-yellow-800';
  };

  const etiquetaSolicitud = (estadoRaw) => {
    const e = String(estadoRaw || '').toLowerCase();
    return (
      {
        pendiente: 'Pendiente de aprobación',
        aprobada: 'Transferida al técnico',
        utilizada: 'Utilizada',
        rechazada: 'Rechazada',
        cancelada: 'Cancelada',
      }[e] || estadoRaw
    );
  };

  const SolicitudesPedido = ({ pedidoId }) => {
    const solicitudes = solicitudesPorPedido[pedidoId] || [];
    if (!esTecnico || solicitudes.length === 0) return null;

    return (
      <div className="mt-3 space-y-2">
        {solicitudes.map((solicitud) => (
          <div key={solicitud.id} className="rounded border border-blue-100 bg-blue-50/60 p-3 text-sm">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-semibold text-gray-800">Solicitud de repuestos #{solicitud.id}</div>
              <span className={`px-2 py-1 rounded text-xs font-bold ${badgeSolicitudClass(solicitud.estado)}`}>
                {etiquetaSolicitud(solicitud.estado)}
              </span>
            </div>

            <div className="mt-2 text-gray-700">
              {(solicitud.detalles || []).map((detalle) => (
                <div key={detalle.id}>
                  {detalle.nombre} · Solicitado: {detalle.cantidad_solicitada}
                  {detalle.cantidad_aprobada ? ` · Aprobado: ${detalle.cantidad_aprobada}` : ''}
                  {detalle.cantidad_utilizada ? ` · Usado: ${detalle.cantidad_utilizada}` : ''}
                </div>
              ))}
            </div>

            {solicitud.estado === 'aprobada' && (
              <button
                type="button"
                onClick={() => usarSolicitudRepuestos(solicitud.id, pedidoId)}
                className="mt-3 w-full rounded bg-green-600 px-3 py-2 text-white hover:bg-green-700"
              >
                Usar repuestos
              </button>
            )}

            {solicitud.observacion_admin && (
              <div className="mt-2 rounded bg-white/80 p-2 text-gray-700">
                <span className="font-semibold">Respuesta admin:</span> {solicitud.observacion_admin}
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const alternarEstadoFiltro = (estado) => {
    setEstadosFiltro((prev) =>
      prev.includes(estado) ? prev.filter((item) => item !== estado) : [...prev, estado]
    );
  };

  const normalizarTexto = (valor) =>
    String(valor || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

  const colegiosFiltrados = useMemo(() => {
    const texto = normalizarTexto(busquedaColegioFiltro.trim());
    if (!texto) return colegios.slice(0, 10);
    return colegios
      .filter((colegio) => normalizarTexto(colegio.nombre).includes(texto))
      .slice(0, 10);
  }, [colegios, busquedaColegioFiltro]);

  const seleccionarColegioFiltro = (colegio) => {
    setColegioFiltro(String(colegio.id));
    setBusquedaColegioFiltro(colegio.nombre);
    setColegioFiltroAbierto(false);
  };

  const limpiarColegioFiltro = () => {
    setColegioFiltro('');
    setBusquedaColegioFiltro('');
    setColegioFiltroAbierto(false);
  };

  const abrirSolicitudRepuestos = (pedido) => {
    setPedidoSolicitud(pedido);
    setDetalleSolicitud({ componente_id: '', cantidad: 1 });
    setDetallesSolicitud([]);
    setObservacionSolicitud('');
  };

  const stockEnDepositoTecnico = (componenteId) => {
    if (!depositoTecnico?.deposito_id) return null;
    const stock = stockDepositos.find(
      (item) =>
        Number(item.componente_id) === Number(componenteId) &&
        Number(item.deposito_id) === Number(depositoTecnico.deposito_id)
    );
    return stock ? Number(stock.cantidad || 0) : 0;
  };

  const cerrarSolicitudRepuestos = () => {
    setPedidoSolicitud(null);
    setDetalleSolicitud({ componente_id: '', cantidad: 1 });
    setDetallesSolicitud([]);
    setObservacionSolicitud('');
    setEnviandoSolicitud(false);
  };

  const agregarDetalleSolicitud = () => {
    const componenteId = Number(detalleSolicitud.componente_id);
    const cantidad = Number(detalleSolicitud.cantidad);
    const componente = componentes.find((item) => Number(item.id) === componenteId);

    if (!componente || !Number.isFinite(cantidad) || cantidad <= 0) {
      alert('Seleccioná un repuesto y una cantidad válida');
      return;
    }

    setDetallesSolicitud((prev) => {
      const existente = prev.find((item) => Number(item.componente_id) === componenteId);
      if (existente) {
        return prev.map((item) =>
          Number(item.componente_id) === componenteId
            ? { ...item, cantidad: Number(item.cantidad) + cantidad }
            : item
        );
      }

      return [
        ...prev,
        {
          componente_id: componenteId,
          cantidad,
          nombre: componente.nombre,
          codigo_unico: componente.codigo_unico,
          stock_actual: componente.cantidad,
          stock_deposito_tecnico: stockEnDepositoTecnico(componenteId),
          deposito_tecnico_nombre: depositoTecnico?.deposito_nombre || '',
        },
      ];
    });

    setDetalleSolicitud({ componente_id: '', cantidad: 1 });
  };

  const quitarDetalleSolicitud = (componenteId) => {
    setDetallesSolicitud((prev) =>
      prev.filter((item) => Number(item.componente_id) !== Number(componenteId))
    );
  };

  const enviarSolicitudRepuestos = async () => {
    if (!pedidoSolicitud?.id || detallesSolicitud.length === 0) {
      alert('Agregá al menos un repuesto');
      return;
    }

    setEnviandoSolicitud(true);

    try {
      const res = await fetch(`${API_BASE}/api/pedidos/${pedidoSolicitud.id}/solicitudes-repuestos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Actor-Rol': localStorage.getItem('rolAdmin') || '',
          'X-Actor-Id': localStorage.getItem('adminId') || '',
        },
        body: JSON.stringify({
          observacion_tecnico: observacionSolicitud,
          detalles: detallesSolicitud.map((item) => ({
            componente_id: item.componente_id,
            cantidad: item.cantidad,
          })),
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data?.mensaje || 'No se pudo enviar la solicitud');
        return;
      }

      alert(data?.mensaje || 'Solicitud enviada correctamente');
      cargarSolicitudesDePedidos([pedidoSolicitud.id]);
      cerrarSolicitudRepuestos();
    } catch (error) {
      console.error('Error enviando solicitud de repuestos:', error);
      alert('Error de conexión al enviar la solicitud');
    } finally {
      setEnviandoSolicitud(false);
    }
  };

  const usarSolicitudRepuestos = async (solicitudId, pedidoId) => {
    const observacion = window.prompt('Observación de uso del repuesto (opcional):');
    if (observacion === null) return;

    try {
      const res = await fetch(`${API_BASE}/api/solicitudes-repuestos/${solicitudId}/usar`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Actor-Rol': localStorage.getItem('rolAdmin') || '',
          'X-Actor-Id': localStorage.getItem('adminId') || '',
        },
        body: JSON.stringify({ observacion_tecnico: observacion || '' }),
      });
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        alert(data?.mensaje || 'No se pudo registrar el uso de repuestos');
        return;
      }

      alert(data?.mensaje || 'Uso registrado correctamente');
      cargarSolicitudesDePedidos([pedidoId]);
    } catch (error) {
      console.error('Error registrando uso de repuestos:', error);
      alert('Error de conexión al registrar uso');
    }
  };

  const recargarPedidos = async () => {
    const res = await fetch(`${API_BASE}/api/pedidos`);
    const data = res.ok ? await res.json() : [];
    setPedidos(Array.isArray(data) ? data : []);
    return Array.isArray(data) ? data : [];
  };

  const asignarTecnico = async (pedido, tecnicoId) => {
    if (!esAdmin || !pedido?.id) return;

    try {
      const res = await fetch(`${API_BASE}/api/pedidos/${pedido.id}/asignar-tecnico`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Actor-Rol': localStorage.getItem('rolAdmin') || '',
          'X-Actor-Id': localStorage.getItem('adminId') || '',
        },
        body: JSON.stringify({ tecnico_id: tecnicoId || null }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data?.mensaje || 'No se pudo asignar el técnico');
        return;
      }

      setPedidos((prev) =>
        prev.map((item) =>
          item.id === pedido.id
            ? {
                ...item,
                asignado_tecnico_id: data.asignado_tecnico_id,
                tecnico_asignado: data.tecnico_asignado,
                fecha_asignacion_tecnico: data.fecha_asignacion_tecnico,
              }
            : item
        )
      );
    } catch (error) {
      console.error('Error asignando técnico:', error);
      alert('Error de conexión al asignar técnico');
    }
  };

  const abrirPedidoAdmin = () => {
    setPedidoAdmin(pedidoAdminInicial);
    setSalasAdmin([]);
    setEquiposAdmin([]);
    setPedidoAdminAbierto(true);
  };

  const cerrarPedidoAdmin = () => {
    setPedidoAdminAbierto(false);
    setPedidoAdmin(pedidoAdminInicial);
    setSalasAdmin([]);
    setEquiposAdmin([]);
    setGuardandoPedidoAdmin(false);
  };

  const actualizarPedidoAdmin = (campo, valor) => {
    setPedidoAdmin((prev) => {
      const siguiente = { ...prev, [campo]: valor };
      if (campo === 'colegio_id') {
        siguiente.sala_id = '';
        siguiente.equipo_id = '';
        setEquiposAdmin([]);
      }
      if (campo === 'sala_id') siguiente.equipo_id = '';
      if (campo === 'tipo_pedido' && valor !== 'Soporte Técnico') siguiente.equipo_id = '';
      return siguiente;
    });
  };

  const guardarPedidoAdmin = async () => {
    const descripcion = pedidoAdmin.descripcion_problema.trim();
    if (!pedidoAdmin.tipo_pedido || !pedidoAdmin.colegio_id || !pedidoAdmin.sala_id || !descripcion) {
      alert('Completá tipo, colegio, sala y descripción.');
      return;
    }
    if (pedidoAdmin.tipo_pedido === 'Soporte Técnico' && !pedidoAdmin.equipo_id) {
      alert('Para soporte técnico seleccioná el equipo.');
      return;
    }

    setGuardandoPedidoAdmin(true);

    try {
      const idempotencyKey =
        window.crypto?.randomUUID?.() || `admin-${Date.now()}-${Math.random().toString(16).slice(2)}`;
      const actorRol = localStorage.getItem('rolAdmin') || '';
      const actorId = localStorage.getItem('adminId') || '';
      const res = await fetch(`${API_BASE}/api/pedidos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Actor-Rol': actorRol,
          'X-Actor-Id': actorId,
          'X-Idempotency-Key': idempotencyKey,
        },
        body: JSON.stringify({
          ...pedidoAdmin,
          profesor_id: null,
          equipo_id: pedidoAdmin.tipo_pedido === 'Soporte Técnico' ? pedidoAdmin.equipo_id : null,
          descripcion_problema: descripcion,
          idempotency_key: idempotencyKey,
          actor_rol: actorRol,
          actor_id: actorId,
        }),
      });

      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data?.mensaje || 'No se pudo crear el pedido');
        return;
      }

      await recargarPedidos();
      cerrarPedidoAdmin();
      alert(data?.duplicado ? 'El pedido ya estaba registrado.' : 'Pedido creado correctamente.');
    } catch (error) {
      console.error('Error creando pedido administrativo:', error);
      alert('Error de conexión al crear el pedido');
    } finally {
      setGuardandoPedidoAdmin(false);
    }
  };

  const eliminarPedido = async (id) => {
    if (esTecnico) return;
    if (!window.confirm('¿Estás seguro de que querés eliminar este pedido?')) return;

    try {
      const res = await fetch(`${API_BASE}/api/pedidos/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('DELETE falló');
      try {
        await res.json();
      } catch {}

      setPedidos((prev) => prev.filter((p) => p.id !== id));
    } catch (error) {
      console.error('No se pudo eliminar el pedido:', error);
      alert('No se pudo eliminar el pedido');
    }
  };

  const abrirModalEstado = (pedido) => {
    if (!pedido?.id) return;
    const estadoActual = String(pedido.estado || '').toLowerCase();
    setPedidoEstado(pedido);
    setEstadoForm({
      estado: ['resuelto', 'en_espera', 'en_laboratorio'].includes(estadoActual)
        ? estadoActual
        : 'resuelto',
      observacion: pedido.observacion_tecnico || '',
    });
  };

  const cerrarModalEstado = () => {
    setPedidoEstado(null);
    setEstadoForm({ estado: 'resuelto', observacion: '' });
    setGuardandoEstado(false);
  };

  // ✅ Definir estado (nuevo endpoint): PUT /api/pedidos/:id/estado
  const definirEstado = async () => {
    const pedido = pedidoEstado;
    if (!pedido?.id) return;

    const nuevoEstado = estadoForm.estado;
    const observacion = estadoForm.observacion;

    const actorRol = String(localStorage.getItem('rolAdmin') || '').toLowerCase();
    const actorId = Number(localStorage.getItem('adminId') || 0);

    if (!actorId) {
      alert('No se pudo identificar tu usuario. Cerrá sesión e ingresá nuevamente antes de cambiar estados.');
      return;
    }

    setGuardandoEstado(true);

    try {
      const res = await fetch(`${API_BASE}/api/pedidos/${pedido.id}/estado`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-Actor-Rol': actorRol,
          'X-Actor-Id': String(actorId),
        },
        body: JSON.stringify({
          estado: nuevoEstado,
          observacion: observacion || '',
          actor_rol: actorRol,
          actor_id: actorId,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        console.error('Error definiendo estado:', data);
        alert(data?.mensaje || 'No se pudo actualizar el estado');
        return;
      }

      // ✅ actualizar UI
      const tecnicoActual = tecnicos.find((tecnico) => Number(tecnico.id) === actorId);

      setPedidos((prev) =>
        prev.map((p) =>
          p.id === pedido.id
            ? {
                ...p,
                estado: nuevoEstado,
                resuelto_por_tecnico_id:
                  nuevoEstado === 'resuelto' && actorId ? actorId : null,
                tecnico_resolvio:
                  nuevoEstado === 'resuelto' && actorId
                    ? data?.tecnico_resolvio || tecnicoActual?.usuario || p.tecnico_resolvio || ''
                    : '',
                fecha_resuelto:
                  nuevoEstado === 'resuelto' ? data?.fecha_resuelto || new Date().toISOString() : null,
              }
            : p
        )
      );

      cerrarModalEstado();
    } catch (error) {
      console.error('No se pudo definir estado:', error);
      alert('Error de conexión al actualizar el estado');
    } finally {
      setGuardandoEstado(false);
    }
  };

  const descargarExcel = () => {
    const params = new URLSearchParams();
    estadosFiltro.forEach((estado) => params.append('estado', estado));
    if (tipoFiltro) params.append('tipo_pedido', tipoFiltro);
    if (colegioFiltro) params.append('colegio_id', colegioFiltro);
    if (tecnicoFiltro) params.append('tecnico_id', tecnicoFiltro);

    window.open(`${API_BASE}/api/exportar-pedidos?${params.toString()}`, '_blank');
  };

  const cargarSolicitudesDePedidos = async (pedidoIds) => {
    if (!esTecnico || !Array.isArray(pedidoIds) || pedidoIds.length === 0) return;

    try {
      const resultados = await Promise.all(
        pedidoIds.map(async (pedidoId) => {
          const res = await fetch(`${API_BASE}/api/pedidos/${pedidoId}/solicitudes-repuestos`);
          const data = res.ok ? await res.json() : [];
          return [pedidoId, Array.isArray(data) ? data : []];
        })
      );

      setSolicitudesPorPedido((prev) => ({
        ...prev,
        ...Object.fromEntries(resultados),
      }));
    } catch (error) {
      console.error('Error cargando solicitudes de repuestos por pedido:', error);
    }
  };

  const descargarPDF = () => {
    const params = new URLSearchParams();
    estadosFiltro.forEach((estado) => params.append('estado', estado));
    if (tipoFiltro) params.append('tipo_pedido', tipoFiltro);
    if (colegioFiltro) params.append('colegio_id', colegioFiltro);
    if (tecnicoFiltro) params.append('tecnico_id', tecnicoFiltro);

    window.open(`${API_BASE}/api/exportar-pedidos-pdf?${params.toString()}`, '_blank');
  };

  useEffect(() => {
    const cargar = async () => {
      try {
        const promesasBase = [
          fetch(`${API_BASE}/api/pedidos`),
          fetch(`${API_BASE}/api/colegios`),
          fetch(`${API_BASE}/api/tecnicos`),
          fetch(`${API_BASE}/api/componentes`),
        ];

        if (esTecnico) {
          promesasBase.push(fetch(`${API_BASE}/api/inventario/stock-depositos`));
          promesasBase.push(fetch(`${API_BASE}/api/tecnicos-depositos`));
        }

        const [resPedidos, resColegios, resTecnicos, resComponentes, resStockDepositos, resTecnicosDepositos] =
          await Promise.all(promesasBase);

        const dataPedidos = resPedidos.ok ? await resPedidos.json() : [];
        const dataColegios = resColegios.ok ? await resColegios.json() : [];
        const dataTecnicos = resTecnicos.ok ? await resTecnicos.json() : [];
        const dataComponentes = resComponentes.ok ? await resComponentes.json() : [];
        const dataStockDepositos = resStockDepositos?.ok ? await resStockDepositos.json() : [];
        const dataTecnicosDepositos = resTecnicosDepositos?.ok ? await resTecnicosDepositos.json() : [];

        setPedidos(Array.isArray(dataPedidos) ? dataPedidos : []);
        setColegios(Array.isArray(dataColegios) ? dataColegios : []);
        setTecnicos(Array.isArray(dataTecnicos) ? dataTecnicos : []);
        setComponentes(Array.isArray(dataComponentes) ? dataComponentes : []);

        if (esTecnico && Array.isArray(dataPedidos)) {
          const adminId = Number(localStorage.getItem('adminId') || 0);
          const tecnicoActual = Array.isArray(dataTecnicosDepositos)
            ? dataTecnicosDepositos.find((item) => Number(item.tecnico_id) === adminId)
            : null;
          setStockDepositos(Array.isArray(dataStockDepositos) ? dataStockDepositos : []);
          setDepositoTecnico(tecnicoActual || null);
          cargarSolicitudesDePedidos(dataPedidos.map((pedido) => pedido.id));
        }
      } catch (error) {
        console.error('Error cargando datos:', error);
        setPedidos([]);
        setColegios([]);
      }
    };

    cargar();
  }, []);

  useEffect(() => {
    if (!pedidoAdmin.colegio_id) {
      setSalasAdmin([]);
      return;
    }

    fetch(`${API_BASE}/api/colegios/${pedidoAdmin.colegio_id}/salas`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setSalasAdmin(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error('Error cargando salas del colegio:', error);
        setSalasAdmin([]);
      });
  }, [pedidoAdmin.colegio_id]);

  useEffect(() => {
    if (!pedidoAdmin.sala_id) {
      setEquiposAdmin([]);
      return;
    }

    fetch(`${API_BASE}/api/salas/${pedidoAdmin.sala_id}/equipos`)
      .then((res) => (res.ok ? res.json() : []))
      .then((data) => setEquiposAdmin(Array.isArray(data) ? data : []))
      .catch((error) => {
        console.error('Error cargando equipos de la sala:', error);
        setEquiposAdmin([]);
      });
  }, [pedidoAdmin.sala_id]);

  const pedidosFiltrados = pedidos
    .filter(
      (p) =>
        estadosFiltro.length === 0 ||
        estadosFiltro.includes(String(p.estado || '').toLowerCase())
    )
    .filter((p) => !tipoFiltro || p.tipo_pedido === tipoFiltro)
    .filter((p) => !colegioFiltro || String(p.colegio_id) === String(colegioFiltro))
    .filter(
      (p) =>
        !tecnicoFiltro ||
        String(p.asignado_tecnico_id || '') === String(tecnicoFiltro) ||
        String(p.resuelto_por_tecnico_id || '') === String(tecnicoFiltro)
    );

  return (
    <div className="p-4 md:p-6">
      <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <h2 className="text-2xl font-bold text-blue-700">Pedidos del Sistema</h2>
        {esAdmin && (
          <button
            type="button"
            onClick={abrirPedidoAdmin}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded w-full md:w-auto"
          >
            Nuevo pedido
          </button>
        )}
      </div>

      {/* FILTROS */}
      <div className="flex flex-col md:flex-row md:flex-wrap gap-3 mb-5">
        <div className="w-full md:w-auto">
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setEstadosFiltro([])}
              className={`px-3 py-2 border rounded text-sm ${
                estadosFiltro.length === 0
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-300'
              }`}
            >
              Todos
            </button>

            {estadosDisponibles.map((estado) => {
              const activo = estadosFiltro.includes(estado.id);

              return (
                <button
                  key={estado.id}
                  type="button"
                  onClick={() => alternarEstadoFiltro(estado.id)}
                  className={`px-3 py-2 border rounded text-sm ${
                    activo
                      ? `${badgeEstadoClass(estado.id)} border-transparent`
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                  aria-pressed={activo}
                >
                  {estado.label}
                </button>
              );
            })}
          </div>
        </div>

        <select
          value={tipoFiltro}
          onChange={(e) => setTipoFiltro(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded w-full md:w-auto"
        >
          <option value="">Todos los tipos</option>
          <option value="Soporte Técnico">Soporte Técnico</option>
          <option value="Soporte de Sala">Soporte de Sala</option>
          <option value="Insumos">Insumos</option>
          <option value="Mobiliario">Mobiliario</option>
          <option value="Kit de Robótica">Kit de Robótica</option>
        </select>

        <div
          className="relative w-full md:w-72"
          onBlur={() => {
            setTimeout(() => setColegioFiltroAbierto(false), 120);
          }}
        >
          <div className="flex gap-2">
            <input
              type="text"
              value={busquedaColegioFiltro}
              onChange={(e) => {
                setBusquedaColegioFiltro(e.target.value);
                setColegioFiltro('');
                setColegioFiltroAbierto(true);
              }}
              onFocus={() => setColegioFiltroAbierto(true)}
              className="px-3 py-2 border border-gray-300 rounded w-full"
              placeholder="Todos los colegios"
              autoComplete="off"
            />

            {busquedaColegioFiltro && (
              <button
                type="button"
                onClick={limpiarColegioFiltro}
                className="px-3 py-2 border border-gray-300 rounded bg-gray-50 text-gray-700"
                title="Limpiar colegio"
              >
                ×
              </button>
            )}
          </div>

          {colegioFiltroAbierto && (
            <div className="absolute z-30 mt-1 w-full bg-white border border-gray-200 rounded shadow-lg max-h-64 overflow-y-auto">
              <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={limpiarColegioFiltro}
                className="w-full text-left px-3 py-2 hover:bg-blue-50 text-gray-700 border-b"
              >
                Todos los colegios
              </button>
              {colegiosFiltrados.length > 0 ? (
                colegiosFiltrados.map((colegio) => (
                  <button
                    key={colegio.id}
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => seleccionarColegioFiltro(colegio)}
                    className="w-full text-left px-3 py-2 hover:bg-blue-50 text-gray-800 border-b last:border-b-0"
                  >
                    {colegio.nombre}
                  </button>
                ))
              ) : (
                <div className="px-3 py-3 text-sm text-gray-500">
                  No se encontraron colegios.
                </div>
              )}
            </div>
          )}
        </div>

        <select
          value={tecnicoFiltro}
          onChange={(e) => setTecnicoFiltro(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded w-full md:w-auto"
        >
          <option value="">Todos los técnicos</option>
          {tecnicos.map((tecnico) => (
            <option key={tecnico.id} value={tecnico.id}>
              {tecnico.usuario}
            </option>
          ))}
        </select>

        <button
          onClick={descargarExcel}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded w-full md:w-auto"
        >
          📥 Excel
        </button>

        <button
          onClick={descargarPDF}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded w-full md:w-auto"
        >
          🖨️ PDF
        </button>
      </div>

      {/* TABLA DESKTOP */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full table-fixed bg-white shadow-md rounded">
          <colgroup>
            <col className="w-[9%]" />
            <col className="w-[13%]" />
            <col className="w-[14%]" />
            <col className="w-[11%]" />
            <col className="w-[8%]" />
            <col className="w-[24%]" />
            <col className="w-[9%]" />
            <col className="w-[12%]" />
            <col className="w-[130px]" />
          </colgroup>
          <thead>
            <tr className="bg-blue-100 text-left">
              <th className="px-3 py-2">Tipo</th>
              <th className="px-3 py-2">Docente</th>
              <th className="px-3 py-2">Colegio</th>
              <th className="px-3 py-2">Sala</th>
              <th className="px-3 py-2">Equipo</th>
              <th className="px-3 py-2">Descripción</th>
              <th className="px-3 py-2">Fecha</th>
              <th className="px-3 py-2">Estado</th>
              <th className="px-3 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pedidosFiltrados.map((p) => (
              <tr key={p.id} className="border-t align-top">
                <td className="px-3 py-3 break-words">{p.tipo_pedido}</td>
                <td className="px-3 py-3 break-words">{p.nombre_docente}</td>
                <td className="px-3 py-3 break-words">{p.colegio}</td>
                <td className="px-3 py-3 break-words">{p.sala}</td>
                <td className="px-3 py-3 break-words">{p.equipo || '-'}</td>
                <td className="px-3 py-3">
                  <div className="mb-1 text-xs font-semibold text-blue-700">#{p.id}</div>
                  <div className="break-words">{p.descripcion_problema}</div>
                  <SolicitudesPedido pedidoId={p.id} />
                </td>
                <td className="px-3 py-3 text-sm">{p.fecha ? new Date(p.fecha).toLocaleString() : '-'}</td>

                <td className="px-3 py-3">
                  <EstadoPedido pedido={p} />
                </td>

                <td className="px-3 py-3 space-y-2">
                  {esAdmin && (
                    <select
                      value={p.asignado_tecnico_id || ''}
                      onChange={(e) => asignarTecnico(p, e.target.value)}
                      className="w-full border border-gray-300 rounded px-2 py-2 text-sm bg-white"
                      title="Asignar técnico"
                    >
                      <option value="">Sin asignar</option>
                      {tecnicos.map((tecnico) => (
                        <option key={tecnico.id} value={tecnico.id}>
                          {tecnico.usuario}
                        </option>
                      ))}
                    </select>
                  )}

                  <button
                    onClick={() => abrirModalEstado(p)}
                    className="bg-blue-600 hover:bg-blue-700 text-white px-2 py-2 rounded text-sm w-full leading-tight"
                    title="Definir estado del pedido"
                  >
                    Definir estado
                  </button>

                  {esTecnico && (
                    <button
                      onClick={() => abrirSolicitudRepuestos(p)}
                      className="bg-green-600 hover:bg-green-700 text-white px-2 py-2 rounded text-sm w-full leading-tight"
                    >
                      Solicitar repuestos
                    </button>
                  )}

                  {!esTecnico && (
                    <button
                      onClick={() => eliminarPedido(p.id)}
                      className="bg-red-500 hover:bg-red-600 text-white px-2 py-2 rounded text-sm w-full leading-tight"
                    >
                      Eliminar
                    </button>
                  )}
                </td>
              </tr>
            ))}

            {pedidosFiltrados.length === 0 && (
              <tr>
                <td colSpan="9" className="text-center py-6 text-gray-500">
                  No hay pedidos con esos filtros.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* TARJETAS MOBILE */}
      <div className="md:hidden space-y-4">
        {pedidosFiltrados.map((p) => (
          <div key={p.id} className="bg-white shadow rounded-lg p-4">
            <div className="flex justify-between items-center">
              <span className="font-semibold text-blue-700">#{p.id}</span>
              <div className="text-right">
                <EstadoPedido pedido={p} />
              </div>
            </div>

            <div className="mt-2 text-sm space-y-1">
              <p>
                <strong>Tipo:</strong> {p.tipo_pedido}
              </p>
              <p>
                <strong>Docente:</strong> {p.nombre_docente}
              </p>
              <p>
                <strong>Colegio:</strong> {p.colegio}
              </p>
              <p>
                <strong>Sala:</strong> {p.sala}
              </p>
              <p>
                <strong>Equipo:</strong> {p.equipo || '-'}
              </p>
            </div>

            <p className="mt-2 text-gray-600 text-sm">{p.descripcion_problema}</p>
            <SolicitudesPedido pedidoId={p.id} />

            <div className="mt-3 text-xs text-gray-500">
              {p.fecha ? new Date(p.fecha).toLocaleString() : '-'}
            </div>

            <div className="flex gap-2 mt-4">
              {esAdmin && (
                <select
                  value={p.asignado_tecnico_id || ''}
                  onChange={(e) => asignarTecnico(p, e.target.value)}
                  className="flex-1 border border-gray-300 rounded px-2 py-2 text-sm bg-white"
                  title="Asignar técnico"
                >
                  <option value="">Sin asignar</option>
                  {tecnicos.map((tecnico) => (
                    <option key={tecnico.id} value={tecnico.id}>
                      {tecnico.usuario}
                    </option>
                  ))}
                </select>
              )}

              <button
                onClick={() => abrirModalEstado(p)}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white py-2 rounded text-sm"
                title="Definir estado del pedido"
              >
                Definir estado
              </button>

              {!esTecnico && (
                <button
                  onClick={() => eliminarPedido(p.id)}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white py-2 rounded text-sm"
                >
                  Eliminar
                </button>
              )}

              {esTecnico && (
                <button
                  onClick={() => abrirSolicitudRepuestos(p)}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white py-2 rounded text-sm"
                >
                  Solicitar repuestos
                </button>
              )}
            </div>
          </div>
        ))}

        {pedidosFiltrados.length === 0 && (
          <div className="text-center py-6 text-gray-500">No hay pedidos con esos filtros.</div>
        )}
      </div>

      {pedidoEstado && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg overflow-hidden">
            <div className="p-5 border-b flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Definir estado</h3>
                <p className="text-sm text-gray-600">
                  Pedido #{pedidoEstado.id} · {pedidoEstado.colegio || 'Sin colegio'}
                </p>
              </div>
              <button
                type="button"
                onClick={cerrarModalEstado}
                className="px-3 py-2 border rounded bg-gray-100 text-gray-700"
              >
                Cerrar
              </button>
            </div>

            <div className="p-5 space-y-4">
              <div className="grid gap-3">
                {[
                  { id: 'resuelto', label: 'Resuelto', detalle: 'El pedido queda cerrado.' },
                  { id: 'en_espera', label: 'En espera', detalle: 'Queda pausado por dependencia externa.' },
                  { id: 'en_laboratorio', label: 'En laboratorio', detalle: 'Queda en revisión técnica.' },
                ].map((estado) => {
                  const activo = estadoForm.estado === estado.id;
                  return (
                    <button
                      key={estado.id}
                      type="button"
                      onClick={() => setEstadoForm((prev) => ({ ...prev, estado: estado.id }))}
                      className={`text-left border rounded-lg p-3 transition ${
                        activo
                          ? 'border-blue-600 bg-blue-50 ring-2 ring-blue-100'
                          : 'border-gray-200 bg-white hover:bg-gray-50'
                      }`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <span className="font-semibold text-gray-900">{estado.label}</span>
                        <span
                          className={`h-4 w-4 rounded-full border ${
                            activo ? 'border-blue-600 bg-blue-600' : 'border-gray-300 bg-white'
                          }`}
                        />
                      </div>
                      <div className="mt-1 text-sm text-gray-500">{estado.detalle}</div>
                    </button>
                  );
                })}
              </div>

              <textarea
                value={estadoForm.observacion}
                onChange={(e) => setEstadoForm((prev) => ({ ...prev, observacion: e.target.value }))}
                className="w-full border px-3 py-2 rounded"
                rows={4}
                placeholder="Observación técnica (opcional)"
              />
            </div>

            <div className="p-5 border-t flex flex-col md:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={cerrarModalEstado}
                className="px-4 py-2 border rounded bg-white text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={definirEstado}
                disabled={guardandoEstado}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
              >
                {guardandoEstado ? 'Guardando...' : 'Guardar estado'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pedidoAdminAbierto && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Nuevo pedido administrativo</h3>
                <p className="text-sm text-gray-600">Registrá un pedido para cualquier colegio.</p>
              </div>
              <button
                type="button"
                onClick={cerrarPedidoAdmin}
                className="px-3 py-2 border rounded bg-gray-100 text-gray-700"
              >
                Cerrar
              </button>
            </div>

            <div className="p-5 grid gap-4">
              <select
                value={pedidoAdmin.tipo_pedido}
                onChange={(e) => actualizarPedidoAdmin('tipo_pedido', e.target.value)}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="Soporte Técnico">Soporte Técnico</option>
                <option value="Soporte de Sala">Soporte de Sala</option>
                <option value="Insumos">Insumos</option>
                <option value="Mobiliario">Mobiliario</option>
                <option value="Kit de Robótica">Kit de Robótica</option>
              </select>

              <select
                value={pedidoAdmin.colegio_id}
                onChange={(e) => actualizarPedidoAdmin('colegio_id', e.target.value)}
                className="w-full border px-3 py-2 rounded"
              >
                <option value="">Seleccionar colegio</option>
                {colegios.map((colegio) => (
                  <option key={colegio.id} value={colegio.id}>
                    {colegio.nombre}
                  </option>
                ))}
              </select>

              <select
                value={pedidoAdmin.sala_id}
                onChange={(e) => actualizarPedidoAdmin('sala_id', e.target.value)}
                className="w-full border px-3 py-2 rounded"
                disabled={!pedidoAdmin.colegio_id}
              >
                <option value="">Seleccionar sala</option>
                {salasAdmin.map((sala) => (
                  <option key={sala.id} value={sala.id}>
                    {sala.nombre}
                  </option>
                ))}
              </select>

              {pedidoAdmin.tipo_pedido === 'Soporte Técnico' && (
                <select
                  value={pedidoAdmin.equipo_id}
                  onChange={(e) => actualizarPedidoAdmin('equipo_id', e.target.value)}
                  className="w-full border px-3 py-2 rounded"
                  disabled={!pedidoAdmin.sala_id}
                >
                  <option value="">Seleccionar equipo</option>
                  {equiposAdmin.map((equipo) => (
                    <option key={equipo.id} value={equipo.id}>
                      {equipo.nombre}
                    </option>
                  ))}
                </select>
              )}

              <textarea
                value={pedidoAdmin.descripcion_problema}
                onChange={(e) => actualizarPedidoAdmin('descripcion_problema', e.target.value)}
                className="w-full border px-3 py-2 rounded"
                rows={4}
                placeholder="Descripción del pedido"
              />
            </div>

            <div className="p-5 border-t flex flex-col md:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={cerrarPedidoAdmin}
                className="px-4 py-2 border rounded bg-white text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={guardarPedidoAdmin}
                disabled={guardandoPedidoAdmin}
                className="px-4 py-2 rounded bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60"
              >
                {guardandoPedidoAdmin ? 'Guardando...' : 'Crear pedido'}
              </button>
            </div>
          </div>
        </div>
      )}

      {pedidoSolicitud && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 border-b flex items-start justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">Solicitar repuestos</h3>
                <p className="text-sm text-gray-600">
                  Pedido #{pedidoSolicitud.id} · {pedidoSolicitud.colegio || 'Sin colegio'}
                </p>
              </div>
              <button
                type="button"
                onClick={cerrarSolicitudRepuestos}
                className="px-3 py-2 border rounded bg-gray-100 text-gray-700"
              >
                Cerrar
              </button>
            </div>

            <div className="p-5 space-y-4">
              {depositoTecnico?.deposito_id ? (
                <div className="rounded border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-800">
                  Depósito asignado: <span className="font-semibold">{depositoTecnico.deposito_nombre}</span>
                  {depositoTecnico.deposito_alias ? ` · ${depositoTecnico.deposito_alias}` : ''}
                </div>
              ) : (
                <div className="rounded border border-yellow-200 bg-yellow-50 px-4 py-3 text-sm text-yellow-800">
                  No tenés un depósito asignado. Las solicitudes quedarán pendientes de aprobación.
                </div>
              )}

              <div className="grid md:grid-cols-[1fr_120px_auto] gap-3">
                <select
                  value={detalleSolicitud.componente_id}
                  onChange={(e) =>
                    setDetalleSolicitud((prev) => ({ ...prev, componente_id: e.target.value }))
                  }
                  className="w-full border px-3 py-2 rounded"
                >
                  <option value="">Seleccionar repuesto</option>
                  {componentes
                    .filter((item) => Number(item.activo) !== 0)
                    .map((item) => {
                      const stockTecnico = stockEnDepositoTecnico(item.id);
                      const textoDeposito =
                        stockTecnico === null ? '' : ` · Mi depósito: ${stockTecnico}`;
                      return (
                        <option key={item.id} value={item.id}>
                          {item.nombre} ({item.codigo_unico}) · Total: {item.cantidad}
                          {textoDeposito}
                        </option>
                      );
                    })}
                </select>

                <input
                  type="number"
                  min="1"
                  value={detalleSolicitud.cantidad}
                  onChange={(e) =>
                    setDetalleSolicitud((prev) => ({ ...prev, cantidad: e.target.value }))
                  }
                  className="w-full border px-3 py-2 rounded"
                  placeholder="Cantidad"
                />

                <button
                  type="button"
                  onClick={agregarDetalleSolicitud}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
                >
                  Agregar
                </button>
              </div>

              <div className="border rounded-lg overflow-hidden">
                {detallesSolicitud.length === 0 ? (
                  <div className="p-4 text-sm text-gray-500">Todavía no agregaste repuestos.</div>
                ) : (
                  <ul className="divide-y">
                    {detallesSolicitud.map((item) => (
                      <li key={item.componente_id} className="p-3 flex items-center justify-between gap-3">
                        <div>
                          <div className="font-semibold text-gray-900">{item.nombre}</div>
                          <div className="text-sm text-gray-500">
                            {item.codigo_unico} · Cantidad: {item.cantidad} · Stock total: {item.stock_actual}
                            {item.stock_deposito_tecnico !== null &&
                              ` · ${item.deposito_tecnico_nombre || 'Mi depósito'}: ${item.stock_deposito_tecnico}`}
                          </div>
                          {item.stock_deposito_tecnico !== null && (
                            <div
                              className={`mt-1 text-xs ${
                                Number(item.stock_deposito_tecnico) >= Number(item.cantidad)
                                  ? 'text-green-700'
                                  : 'text-orange-700'
                              }`}
                            >
                              {Number(item.stock_deposito_tecnico) >= Number(item.cantidad)
                                ? 'Disponible en tu depósito: se descontará automáticamente al enviar.'
                                : 'No alcanza en tu depósito: se enviará como solicitud al administrador.'}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => quitarDetalleSolicitud(item.componente_id)}
                          className="px-3 py-1 border rounded text-red-700 bg-red-50"
                        >
                          Quitar
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <textarea
                value={observacionSolicitud}
                onChange={(e) => setObservacionSolicitud(e.target.value)}
                className="w-full border px-3 py-2 rounded"
                rows={3}
                placeholder="Observación para el administrador (opcional)"
              />
            </div>

            <div className="p-5 border-t flex flex-col md:flex-row justify-end gap-3">
              <button
                type="button"
                onClick={cerrarSolicitudRepuestos}
                className="px-4 py-2 border rounded bg-white text-gray-700"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={enviarSolicitudRepuestos}
                disabled={enviandoSolicitud}
                className="px-4 py-2 rounded bg-green-600 hover:bg-green-700 text-white disabled:opacity-60"
              >
                {enviandoSolicitud ? 'Procesando...' : 'Usar / solicitar repuestos'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ListaPedidos;
